import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { cn } from '@/shared/lib/cn';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { crearCierreCaja, getCajaSaldo } from '../api';
import { CajaNombre } from './CajaNombre';
import { MoneyInput } from '@/features/ventas/components/MoneyInput';

/**
 * Cierre de Caja (cash closure + arqueo). Matches PowerApps
 * Screen_Tesoreria PopUp_CierreCaja — admin enters the physical cash
 * count, the dialog diffs it against the computed book balance, and on
 * confirm the backend writes:
 *   1. An "Ajuste por arqueo" row if there's a variance.
 *   2. The Cierre row (today).
 *   3. The Apertura row (tomorrow) with the same balance.
 *   4. Tags every prior period row with the new `IDCierreCaja_MC`.
 *
 * **Policy**: the cierre is blocked while the diferencia between the
 * physical count and the Saldo Digital is non-zero. The PowerApps
 * design treated arqueo variances as a normal operating reality and
 * wrote an "Ajuste por arqueo" row automatically — but in practice
 * that hid bookkeeping mistakes (missed movements, typos) until much
 * later. The new flow forces the admin to investigate the variance
 * first; only when the count matches the system balance does the
 * Confirmar button enable.
 */
export function CierreCajaDialog({
  caja,
  moneda,
  onClose,
}: {
  caja: string;
  moneda: 'ARS' | 'MXN' | 'USD';
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [balanceFisico, setBalanceFisico] = useState<string>('');

  // Cross-month current balance for the caja — source of truth for the
  // arqueo diff. The page-level `totals.saldoActual` is month-scoped,
  // which isn't what Cierre is meant to close against.
  // `gcTime: 0` + `refetchOnMount: 'always'` so a previously-cached
  // saldo can never be reused — admins must reconcile against the
  // current book balance, not a stale snapshot.
  const saldoQ = useQuery({
    queryKey: ['tesoreria', 'saldo', caja],
    queryFn: () => getCajaSaldo(caja),
    gcTime: 0,
    refetchOnMount: 'always',
  });
  const saldoBook = saldoQ.data?.saldo ?? 0;

  // Don't seed from the book balance — auto-filling defeats the
  // purpose of the arqueo. The admin has to count the cash and type
  // the real total; matching the digital balance is what unlocks the
  // Confirmar button (see `canSubmit` below).
  const balanceNum = parseMoney(balanceFisico) ?? 0;
  const diferencia = useMemo(
    () => Math.round((balanceNum - saldoBook) * 100) / 100,
    [balanceNum, saldoBook],
  );
  const hasVariance = Math.abs(diferencia) > 0.01;
  const ajusteTipo = diferencia > 0 ? 'Ingreso' : 'Egreso';

  const mutation = useMutation({
    mutationFn: () =>
      crearCierreCaja({
        caja,
        balanceFisico: balanceNum,
      }),
    onSuccess: (r) => {
      const note = r.rowsTagged > 0 ? ` · ${r.rowsTagged} movimiento(s) en el período.` : '';
      toast.success('Caja cerrada.', {
        description: `Balance: ${formatMoney(r.balanceFisico, moneda)}${note}`,
      });
      qc.invalidateQueries({ queryKey: ['tesoreria'] });
      onClose();
    },
    onError: (err) =>
      toast.error('No se pudo cerrar la caja', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  // Cierre is only allowed when the physical count matches the system
  // balance exactly (penny-tolerant). Variance must be reconciled
  // through a regular movement before closing.
  const canSubmit = balanceNum >= 0 && !hasVariance && !mutation.isPending;

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-md"
        processing={mutation.isPending && 'Cerrando caja…'}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" aria-hidden />
            Cerrar <CajaNombre caja={caja} />
          </DialogTitle>
          <DialogDescription>
            Contá físicamente el efectivo disponible y confirmá. Se creará un cierre
            más la apertura del próximo día, y se etiquetarán todos los movimientos del
            período con un identificador de cierre.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="rounded-xl border bg-muted/20 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Saldo Digital</span>
              {saldoQ.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
              ) : (
                <span className="font-semibold text-foreground">
                  {formatMoney(saldoBook, moneda)}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            {/* QA #24: explicit label so admins know they're entering
                the *physically counted* cash, not the system balance. */}
            <Label htmlFor="cierre-balance">Efectivo físico contado ({moneda})</Label>
            <MoneyInput
              id="cierre-balance"
              variant="filled"
              placeholder="0,00"
              value={balanceFisico}
              moneda={moneda}
              onChange={setBalanceFisico}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Contá el dinero en caja y escribí el total exacto. El
              cierre sólo se habilita cuando coincide con el Saldo
              Digital.
            </p>
          </div>

          <div
            className={cn(
              'rounded-xl border p-3 text-sm',
              !hasVariance
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-rose-200 bg-rose-50 text-rose-900',
            )}
          >
            <div className="flex items-center justify-between">
              <span>Diferencia</span>
              <span className="font-semibold">
                {diferencia >= 0 ? '+' : ''}
                {formatMoney(diferencia, moneda)}
              </span>
            </div>
            {hasVariance && (
              <p className="mt-2 text-xs">
                No se puede cerrar la caja con una diferencia de{' '}
                {formatMoney(Math.abs(diferencia), moneda)} ({ajusteTipo}).
                Revisá los movimientos del período antes de continuar.
              </p>
            )}
          </div>

          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Una vez confirmado, los movimientos del período quedan asociados al cierre.
            Las futuras ediciones/anulaciones se bloquearán sobre esas filas.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            title={
              hasVariance
                ? 'No se puede cerrar la caja mientras haya diferencia con el Saldo Digital.'
                : undefined
            }
            className="gap-2 bg-foreground text-background hover:bg-foreground/90"
          >
            Confirmar cierre
            <Check className="h-4 w-4" aria-hidden />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

